import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { GenericModalService, ModalSettingsMessage } from '../../shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ArtifactAssemblyTypes, AssemblyArtifact } from '../models/artifact';
import { IntegratedFtpProviderEntity } from '../models/integrated-ftp-provider';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { DeveloperService } from '../services/developer.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DeveloperArtifactAssemblyPickerComponent } from '../developer-artifacts/developer-artifact-assembly-picker.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { BizDateColumnDirective } from 'src/app/shared/grid/biz-grid-date.directive';
import { BizUserColumnDirective } from 'src/app/shared/grid/biz-grid-user.directive';
import { GridService } from '../../shared/grid/grid.service';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';

@Component({
    selector: 'app-developer-integrated-ftp-providers',
    templateUrl: './developer-integrated-ftp-providers.component.html',
    styleUrls: ['./developer-integrated-ftp-providers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule,
         DxiColumnModule, DxiValidationRuleModule, DxiButtonModule, DxTemplateModule, DeveloperArtifactAssemblyPickerComponent, BizUserColumnDirective, BizDateColumnDirective]
})
export class DeveloperIntegratedFtpProvidersComponent implements OnInit {
    hasCreatePerm = false;
    hasEditPerm = false;
    hasDeletePerm = false;
    hasShowLogPerm = false;

    integratedFtpProviders: IntegratedFtpProviderEntity[] = [];
    artifacts: AssemblyArtifact[] = [];

    constructor(
        private developerService: DeveloperService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private loadingPanelService: LoadingPanelService,
        private modalService: GenericModalService,
        private developerArtifactService: DeveloperArtifactService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {
         this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
         this.allowDeletingRow = this.allowDeletingRow.bind(this);
         this.allowShowLog = this.allowShowLog.bind(this);
         this.displayExprArtifact = this.displayExprArtifact.bind(this);
    }

    async ngOnInit() {
        this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeIntegratedFtpProvider, ArtifactAssemblyTypes.TypeIntegratedFtpProvider2]);
        this.hasCreatePerm = this.currentUserService.hasPermission('Developer_Action_IntegratedFtpProvider_Create');
        this.hasEditPerm = this.currentUserService.hasPermission('Developer_Action_IntegratedFtpProvider_Edit');
        this.hasDeletePerm = this.currentUserService.hasPermission('Developer_Action_IntegratedFtpProvider_Delete');
        this.hasShowLogPerm = this.currentUserService.hasPermission('Developer_Action_ShowLog');
        this.refresh();
    }

    allowUpdatingRow(options: { component: any; row: any; }): boolean {
        const item: IntegratedFtpProviderEntity = options?.row?.data;
        if (item) {
            return this.hasEditPerm;
        }
        return false;
    }

    allowDeletingRow(options: { component: any; row: any; }): boolean {
        const item: IntegratedFtpProviderEntity = options?.row?.data;
        if (item) {
            return this.hasDeletePerm;
        }
        return false;
    }

    allowShowLog(options: { component: any; row: any; }): boolean {
        const item: IntegratedFtpProviderEntity = options?.row?.data;
        if (item) {
            return this.hasShowLogPerm;
        }
        return false;
    }

    displayExprArtifact(e: any) {
        // TODO: Find en bedre måde at håndtere dette, så det ikke er nødvendigt at loade alle artifacts...
        if (this.artifacts) {
            var artifact = this.artifacts.find(a => a.ArtifactId == e.value);
            if (artifact) {
                return `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]`;
            }
        }
        return "Unknown...";
    }

    onInitNewRow(e: DxDataGridTypes.InitNewRowEvent<IntegratedFtpProviderEntity>): void {
        e.data = { Id: null, Name: '', Description: '' } as IntegratedFtpProviderEntity;
    }

    updateDatasource(originalProvider: IntegratedFtpProviderEntity, newProvider: IntegratedFtpProviderEntity): void {
        let originalItem = this.integratedFtpProviders.find(i => i.Id == originalProvider.Id);
            if (originalItem) {
                originalItem.Id = newProvider.Id;
                originalItem.Description = newProvider.Description;
                originalItem.ArtifactId = newProvider.ArtifactId;
            }
    }

    async refresh() {
        this.loadingPanelService.showLoadingPanelDirect('Loading integrated ftp providers...');
        this.integratedFtpProviders = await this.developerService.getIntegratedFtpProviders();
        this.loadingPanelService.hideLoadingPanel();
    }

    onRowInserting = (e: DxDataGridTypes.RowInsertingEvent<IntegratedFtpProviderEntity>): void => {
        const integratedFtpProviderEntity = e.data;
        // Check if another step is already using the artifact/step-key
        const existing = this.integratedFtpProviders.find(i => i.Id !== integratedFtpProviderEntity.Id && i.ArtifactId === integratedFtpProviderEntity.ArtifactId);
            if (existing) {
                e.cancel = true;
                const settings = new ModalSettingsMessage();
                settings.size = 'lg';
                settings.backdrop = 'static';
                // Make sure we are on top with this modal!
                settings.windowClass = 'ngb-modal-on-top';
                settings.backdropClass = 'ngb-modal-on-top-backdrop';
                settings.title = 'Already exist';
                settings.body = 'Another provider already exists for this artifact. Name of the existing provider: ' + existing.Name;
                this.modalService.message(settings);
            } else {
                e.cancel = this.gridService.handleDataGridRow(e.data, item => this.saveIntegratedFtpProvider(item));
            }
    };

    onRowUpdating = (e: DxDataGridTypes.RowUpdatingEvent<IntegratedFtpProviderEntity>): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.oldData, ...e.newData }, item => this.saveIntegratedFtpProvider(item), e.newData);
    };

    onRowRemoving = (e: DxDataGridTypes.RowRemovingEvent<IntegratedFtpProviderEntity>): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.removeIntegratedFtpProvider(item));
    };

    async saveIntegratedFtpProvider(entity: IntegratedFtpProviderEntity): Promise<IntegratedFtpProviderEntity> {
        try {
            const savedItem = await this.developerService.saveIntegratedFtpProviders(entity);
            this.toastService.showToast('Integrated ftp provider saved...', ToastType.success);
            return savedItem;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save integrated ftp provider...', ToastType.error);
            return undefined;
        }
    }

    async removeIntegratedFtpProvider(item: IntegratedFtpProviderEntity): Promise<IntegratedFtpProviderEntity> {
        await this.developerService.deleteIntegratedFtpProvider(item.Id);
        this.toastService.showToast('Integrated ftp provider deleted...', ToastType.success);
        return item;
    }

    async setArtifactId(artifactId: string, cellInfo: any) {
        cellInfo.setValue(artifactId || '');
        if (cellInfo.data.ArtifactId != artifactId && artifactId) { // Only check artifact if it has changed!
            try {
                this.loadingPanelService.showLoadingPanelDirect('Verifying artifact...');
                //const itineraryStepKey = artifactId ? await this.developerService.getItineraryStepKey(artifactId) : null;
                //cellInfo.data.StepKey = itineraryStepKey;
                //cellInfo.component.cellValue(cellInfo.row.rowIndex, 'StepKey', itineraryStepKey);
            } finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
    }
}
